/**
 * result-bridge.ts — per-metric `UnitInfo` ComputedRefs reading the live
 * engine snapshot through `formatUnit`.
 *
 * Jitter rides ping's phase but carries its own numeric — exposed as a
 * `UnitInfo` for symmetry so consumers read a uniform
 * `Record<MetricId, ComputedRef<UnitInfo>>` rather than a switch with a
 * special jitter branch. The variant `FormatPolicy` threads through the
 * bridge so DPI's Mbps lock applies to the store-internal reads, not
 * just the composable seam. `resultByMetric` is the canonical lookup;
 * `resultFor(id)` is the typed accessor.
 */

import { computed, type ComputedRef, type Ref } from "vue";

import type { SpeedtestResults as EngineResults } from "@speedtest/index.ts";
import type { MetricId } from "@src/types/speedtest.ts";
import type { VariantConfig } from "@src/types/variant.ts";

import type { LiveTestData, UnitInfo } from "./types.ts";
import { formatUnit, type FormatPolicy } from "./metric-format.ts";

export interface ResultBridge {
    pingResult: ComputedRef<UnitInfo>;
    jitterResult: ComputedRef<UnitInfo>;
    downloadResult: ComputedRef<UnitInfo>;
    uploadResult: ComputedRef<UnitInfo>;
    resultByMetric: ComputedRef<Record<MetricId, UnitInfo>>;
    resultFor: (metricId: MetricId) => UnitInfo;
}

/** Derive the formatter policy from a variant. The DPI variant locks
 *  throughput to Mbps; latency is auto across all variants. Default
 *  variants get `{}` so the formatter walks both ladders. */
export function policyForVariant(variant: VariantConfig | null): FormatPolicy {
    if (variant?.id === "dpi") return { throughput: "lock-mbps" };
    return {};
}

export function createResultBridge(
    data: Ref<LiveTestData | null>,
    results: Ref<EngineResults | null>,
    variant?: Ref<VariantConfig> | ComputedRef<VariantConfig>,
): ResultBridge {
    const policy: ComputedRef<FormatPolicy> = computed(() =>
        policyForVariant(variant?.value ?? null),
    );

    // X5 null-honesty: each metric reads ONE honest source — the live
    // engine value, else the persisted result — and emits an empty
    // `UnitInfo` (`{}`) when both are null, mirroring the jitter branch
    // below. The prior coercion fabricated a falsy zero pre-test, which the
    // smoother/formatter rendered as a literal "0" pill instead of the
    // AS-2/3 placeholder em-dash. The empty `UnitInfo` propagates as a null
    // `rawAmount`, so `useMetricFormatter` collapses to the honest em-dash.
    const pingResult: ComputedRef<UnitInfo> = computed(() => {
        const v = data.value?.ping ?? results.value?.ping.latency;
        return v != null ? formatUnit("ping", v, policy.value) : {};
    });

    const downloadResult: ComputedRef<UnitInfo> = computed(() => {
        const v =
            data.value?.downloadSpeed ?? results.value?.download.speedMbps;
        return v != null ? formatUnit("download", v, policy.value) : {};
    });

    const uploadResult: ComputedRef<UnitInfo> = computed(() => {
        const v = data.value?.uploadSpeed ?? results.value?.upload.speedMbps;
        return v != null ? formatUnit("upload", v, policy.value) : {};
    });

    const jitterResult: ComputedRef<UnitInfo> = computed(() => {
        const j = data.value?.jitter ?? results.value?.ping.jitter;
        return j != null ? formatUnit("jitter", j, policy.value) : {};
    });

    const resultByMetric: ComputedRef<Record<MetricId, UnitInfo>> = computed(
        () => ({
            ping: pingResult.value,
            jitter: jitterResult.value,
            download: downloadResult.value,
            upload: uploadResult.value,
        }),
    );

    function resultFor(metricId: MetricId): UnitInfo {
        return resultByMetric.value[metricId];
    }

    return {
        pingResult,
        jitterResult,
        downloadResult,
        uploadResult,
        resultByMetric,
        resultFor,
    };
}
